import React, { Fragment, ReactNode } from 'react'

import InfoHint from '../../../../common/InfoHint'
import { MerchantActiveCurrency } from './MerchantActiveCurrency'
import { MerchantBalancePolicy } from './MerchantBalancePolicy'
import MerchantVariantFallback from './MerchantVariantFallback'
import MerchantDepositPrecheck from './MerchantDepositPrecheck'
import MerchantDepositSupersede from './MerchantDepositSupersede'
import MerchantPaymentPageTimeout from './MerchantPaymentPageTimeout'
import MerchantMatchingAutoHalt from './MerchantMatchingAutoHalt'
import { MERCHANT_SETTING_HINTS, MerchantSettingKey } from './settingsHints'
import { IMerchant } from './types'

interface Props {
  merchant: IMerchant
  canEditMerchantSettings: boolean
}

interface SettingItem {
  key: MerchantSettingKey
  label: string
  visible: boolean
  control: ReactNode
}

// Label over the control below md, side by side from md up — a wide control never pushes the page sideways.
const SettingRow: React.FC<{ label: string; hint: string; children: ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div className='row align-items-center gy-2' data-testid='merchant-setting-row'>
    <div className='col-md-4'>
      <span className='fw-bold text-gray-600 fs-7 text-uppercase' data-testid='merchant-setting-label'>
        {label}
      </span>
      <InfoHint text={hint} />
    </div>
    <div className='col-md-8'>{children}</div>
  </div>
)

export const MerchantSettings: React.FC<Props> = ({ merchant, canEditMerchantSettings }) => {
  const items: SettingItem[] = [
    {
      key: 'activeCurrency',
      label: 'Активная валюта',
      visible: true,
      control: <MerchantActiveCurrency merchantId={merchant.id} />,
    },
    {
      key: 'balancePolicy',
      label: 'Учёт баланса',
      visible: true,
      control: <MerchantBalancePolicy merchantId={merchant.id} />,
    },
    {
      key: 'variantFallback',
      label: 'Fallback вариантов депозита',
      visible: true,
      control: (
        <MerchantVariantFallback
          merchantId={merchant.id}
          pgEnabled={!!merchant.variantFallbackPgEnabled}
          h2hEnabled={!!merchant.variantFallbackH2hEnabled}
        />
      ),
    },
    {
      key: 'depositPrecheck',
      label: 'Проверка матча при создании депозита',
      visible: canEditMerchantSettings,
      control: (
        <MerchantDepositPrecheck merchantId={merchant.id} enabled={!!merchant.depositPrecheckEnabled} />
      ),
    },
    {
      key: 'depositSupersede',
      label: 'Гашение дублей депозитов клиента',
      visible: canEditMerchantSettings,
      control: (
        <MerchantDepositSupersede
          merchantId={merchant.id}
          enabled={merchant.depositSupersedeEnabled ?? true}
        />
      ),
    },
    {
      key: 'paymentPageTimeout',
      label: 'Платёжная страница: таймаут (мин)',
      visible: canEditMerchantSettings,
      control: (
        <MerchantPaymentPageTimeout
          merchantId={merchant.id}
          minutes={merchant.paymentPageTimeoutMinutes ?? null}
        />
      ),
    },
    {
      key: 'matchingAutoHalt',
      label: 'Мэтчинг: автоостановка выплаты (клиентов)',
      visible: canEditMerchantSettings,
      control: (
        <MerchantMatchingAutoHalt
          merchantId={merchant.id}
          clients={merchant.matchingAutoHaltClients ?? null}
        />
      ),
    },
  ]

  const visible = items.filter((item) => item.visible)

  return (
    <div className='card mb-7'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Настройки</span>
        </h3>
      </div>
      <div className='card-body py-4'>
        {visible.map((item, i) => (
          <Fragment key={item.key}>
            {i > 0 && <div className='separator separator-dashed my-5' />}
            <SettingRow label={item.label} hint={MERCHANT_SETTING_HINTS[item.key]}>
              {item.control}
            </SettingRow>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

export default MerchantSettings
